import { Button, Icon, Popover, PopoverContent, TextBox } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { useStore } from '../../hooks/use-store';
import { CsVersion } from '../../types/practice-types';
import { semverLessThan } from '../../utils/semver-utils';

const MINIMAL_REBOOT_TIMEOUT_VERSION = '9.7';

interface IndexProps {
  index: number;
}

interface EditFieldRendererProps {
  object: CsVersion;
  field: keyof CsVersion;
  numeric?: boolean;
  disabled?: boolean;
  maxLength?: number;
  editMode: boolean;
}

const EditFieldRenderer = observer((props: EditFieldRendererProps) => {
  const { object, numeric, field, disabled, maxLength, editMode } = props;
  const store = useStore().upgradeVersionManagementStore;
  const { validation } = store;

  const changeValue = (newValue: string): void => {
    if (numeric && !/^\d+/.test(newValue)) return;
    runInAction(() => {
      // @ts-ignore
      object[field] = numeric ? parseInt(newValue) : newValue;
    });
  };

  return editMode ? (
    <FieldGroupInCell htmlFor={`edit-text-box_${field}`} errorText={validation[field]}>
      <TextBox
        id={`edit-text-box_${field}`}
        data-testid={`edit-text-box_${field}`}
        value={String(object[field])}
        maxLength={maxLength}
        onChange={e => changeValue(e.target.value)}
        disabled={disabled}
      />
    </FieldGroupInCell>
  ) : (
    <strong>{object[field]}</strong>
  );
});

export const UpgradeVersionContent = observer((props: IndexProps) => {
  const { index } = props;
  const store = useStore().upgradeVersionManagementStore;
  const { versions, currentVersion, selectedVersionIndex, isNewVersion } = store;
  const editMode = store.editMode && selectedVersionIndex === index;
  const csVersion = editMode ? currentVersion : versions[index];

  return (
    <>
      <ul>
        <li>
          Version Number:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='version'
            maxLength={20}
            disabled={!isNewVersion}
            editMode={editMode}
          />
        </li>
        <li>
          Installer Name:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='installerName'
            maxLength={50}
            editMode={editMode}
          />
        </li>
        <li>
          Installer Zip Name:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='installerZipName'
            maxLength={50}
            editMode={editMode}
          />
        </li>
        <li>
          Installer Size (GB):{' '}
          <EditFieldRenderer
            object={csVersion}
            field='installerSize'
            maxLength={10}
            numeric
            editMode={editMode}
          />
        </li>
        <li>
          Installer Arguments:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='installerArgs'
            maxLength={50}
            editMode={editMode}
          />
        </li>
      </ul>
      <ul>
        <li>
          Minimum Supported Version:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='minimalUpgradeVersion'
            maxLength={20}
            editMode={editMode}
          />
        </li>
        <li>
          Supported Operating Systems:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='osPattern'
            maxLength={30}
            editMode={editMode}
          />
        </li>
        <li>
          Supported CPU Families:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='cpuPattern'
            maxLength={30}
            editMode={editMode}
          />
        </li>
        <li>
          Kaseya Qualification Procedure:{' '}
          <EditFieldRenderer
            object={csVersion}
            field='qualificationAPs'
            maxLength={50}
            editMode={editMode}
          />
        </li>
        {semverLessThan(MINIMAL_REBOOT_TIMEOUT_VERSION, csVersion.version) && (
          <li>
            Mid-upgrade Reboot Time (Minutes):{' '}
            <EditFieldRenderer
              object={csVersion}
              field='rebootTimeout'
              maxLength={10}
              numeric
              editMode={editMode}
            />
          </li>
        )}
      </ul>
      <ul>
        <li>
          Server RAM (GB):{' '}
          <EditFieldRenderer
            object={csVersion}
            field='serverRam'
            maxLength={10}
            numeric
            editMode={editMode}
          />
        </li>
        <li>
          Server Disk Capacity (GB):{' '}
          <EditFieldRenderer
            object={csVersion}
            field='serverDiskSize'
            maxLength={10}
            numeric
            editMode={editMode}
          />
        </li>
        <li>
          Workstation RAM (GB):{' '}
          <EditFieldRenderer
            object={csVersion}
            field='workstationRam'
            maxLength={10}
            numeric
            editMode={editMode}
          />
        </li>
        <li>
          Workstation Disk Capacity (GB):{' '}
          <EditFieldRenderer
            object={csVersion}
            field='workstationDiskSize'
            maxLength={10}
            numeric
            editMode={editMode}
          />
        </li>
      </ul>
    </>
  );
});

export const PopoverRenderer = observer((props: IndexProps) => {
  const { index } = props;
  const store = useStore().upgradeVersionManagementStore;
  const { loaders, versions } = store;
  const item = versions[index];

  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <div className='actions-popover'>
      <Popover
        side='bottom'
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        data-testid={`actions-${index}-popover`}
        content={
          <PopoverContent size='small'>
            <Button
              variant='link'
              size='small'
              disabled={loaders.save}
              onClick={() => {
                store.startEdit(index);
                setIsOpen(false);
              }}
              data-testid={`edit-${index}-link`}
            >
              Edit
            </Button>
            <Button
              variant='link'
              size='small'
              disabled={loaders.save}
              onClick={() => {
                store.cloneAndEdit(index);
                setIsOpen(false);
              }}
              data-testid={`clone-${index}-link`}
            >
              Clone
            </Button>
            <Button
              variant='link'
              size='small'
              disabled={loaders.save || item.default}
              onClick={() => {
                void store.makeDefault(index);
                setIsOpen(false);
              }}
              data-testid={`make-default-${index}-link`}
            >
              Make Default
            </Button>
            <Button
              variant='link'
              size='small'
              disabled={loaders.save || !item.isDeletable || item.default}
              onClick={() => {
                store.showDeleteModal(index);
                setIsOpen(false);
              }}
              data-testid={`delete-${index}-link`}
            >
              Delete
            </Button>
          </PopoverContent>
        }
      >
        <span>
          <Icon
            role='icon-button'
            size='l'
            aria-label='CS Version Ellipsis'
            iconName='more-2'
            title='Click the action menu to access version specific options.'
          />
        </span>
      </Popover>
    </div>
  );
});
